import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from '@/components/ui/dialog';
import LeadForm from '@/components/LeadForm';
import { Link } from 'react-router-dom';
import { Linkedin, Facebook, Instagram } from 'lucide-react';
import { NAP, AU_STATES, getHQ, getSatellites } from '@/constants/locations';

const MethodologyStep = ({ index, title, desc }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { margin: "-20% 0px -20% 0px" });

  return (
    <motion.div
      ref={ref}
      className={cn(
        "flex-1 p-8 border-t-4 transition-all duration-500 flex flex-col gap-4 relative overflow-hidden",
        isInView ? "border-[#4A90E2] bg-[#2D323E] shadow-[0_0_20px_rgba(74,144,226,0.1)]" : "border-gray-700 bg-[#1F232B] opacity-60"
      )}
    >
      <span className={cn(
        "text-6xl font-title font-bold absolute top-2 right-4 transition-colors duration-500 select-none",
        isInView ? "text-[#4A90E2]/20" : "text-white/5"
      )}>
        0{index}
      </span>
      <h4 className={cn(
        "font-heading font-bold text-xl uppercase tracking-wide z-10 transition-colors",
        isInView ? "text-white" : "text-gray-400"
      )}>
        {title}
      </h4>
      <p className="font-body text-base text-gray-300 z-10 leading-relaxed">
        {desc}
      </p>
    </motion.div>
  );
};

export default function Footer() {
  const steps = [
    { title: "STRATEGY", desc: "We conduct audits and define governance—ensuring your tech serves your P&L." },
    { title: "ARCHITECTURE", desc: "We design secure-by-design technical roadmaps and cloud platforms that scale with your growth." },
    { title: "EXECUTION", desc: "We implement actual systems and automation that automates your unique business workflows." }
  ];

  return (
    <footer className="bg-[#1A1D21] text-white border-t-2 border-[#4A90E2]" aria-label="Site Map and Conversion">
      {/* Hybrid Methodology Strip */}
      <div className="container mx-auto px-4 py-20">
        <div className="flex flex-col md:flex-row gap-4 mb-20">
          {steps.map((step, i) => (
            <MethodologyStep key={i} index={i + 1} {...step} />
          ))}
        </div>

        {/* Dual-Path CTA */}
        <div className="bg-[#2D323E] rounded-[2px] p-12 border-2 border-[#4A90E2] relative overflow-hidden">
          {/* Middle Layer Visual - Glowing Line */}
          <div className="absolute top-1/2 left-0 w-full h-[1px] bg-gradient-to-r from-transparent via-[#4A90E2] to-transparent opacity-50 hidden md:block pointer-events-none"></div>
          
          <div className="max-w-5xl mx-auto text-center relative z-10">
            <h2 className="font-title text-5xl md:text-6xl mb-12 uppercase tracking-wide text-white">What is your bottleneck?</h2>
            
            <div className="flex flex-col md:flex-row items-center justify-center gap-12 md:gap-24">
              
              {/* Path A: The Enterprise Leader */}
              <div className="flex flex-col items-center gap-6 group">
                <span className="font-mono text-[#9e591e] text-sm tracking-widest uppercase mb-2 group-hover:text-white transition-colors">PATH A: THE ENTERPRISE LEADER</span>
                <Button 
                  asChild
                  variant="outline"
                  size="lg" 
                  className="border-2 border-[#9e591e] text-[#9e591e] hover:bg-[#9e591e] hover:text-white rounded-[2px] h-16 px-10 text-lg font-heading uppercase tracking-wide transition-all"
                >
                  <Link to="/contact">REQUEST STRATEGY AUDIT</Link>
                </Button>
              </div>

              {/* Path B: The Founder */}
              <div className="flex flex-col items-center gap-6 group">
                <span className="font-mono text-[#4A90E2] text-sm tracking-widest uppercase mb-2 group-hover:text-white transition-colors">PATH B: THE FOUNDER</span>
                <Dialog>
                  <DialogTrigger asChild>
                    <Button 
                      size="lg" 
                      className="bg-[#4A90E2] text-white hover:bg-[#3a7bc8] rounded-[2px] h-16 px-10 text-lg font-heading uppercase tracking-wide shadow-block hover:-translate-y-1 transition-transform border-2 border-[#4A90E2]"
                    >
                      REQUEST SOLUTION DESIGN
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-[#1F232B] border-2 border-[#4A90E2] text-white rounded-[2px]">
                    <DialogHeader>
                      <DialogTitle className="font-title text-3xl uppercase text-[#4A90E2]">Solution Design</DialogTitle>
                      <DialogDescription className="text-gray-400">
                        Execute your vision with scalable architecture.
                      </DialogDescription>
                    </DialogHeader>
                    <LeadForm defaultIntent="Solution Design" hideIntent={true} />
                  </DialogContent>
                </Dialog>
              </div>

            </div>
          </div>
        </div>
      </div>

      {/* Operational Log Metadata Footer */}
      <div className="border-t border-gray-800 bg-[#15171A] py-12">
        <div className="container mx-auto px-4">
          {/* Footer Columns */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-8 text-sm">
            {/* Company */}
            <div>
              <h4 className="font-heading font-bold text-white uppercase mb-4 tracking-wider">Company</h4>
              <ul className="space-y-2 font-mono text-gray-400">
                <li><Link to="/about" className="hover:text-[#4A90E2] transition-colors">About</Link></li>
                <li><Link to="/services" className="hover:text-[#4A90E2] transition-colors">Services</Link></li>
                <li><Link to="/intelligence" className="hover:text-[#4A90E2] transition-colors">Intelligence</Link></li>
                <li><Link to="/assessment" className="hover:text-[#4A90E2] transition-colors" data-testid="footer-assessment-link">AI Maturity Quiz</Link></li>
                <li><Link to="/contact" className="hover:text-[#4A90E2] transition-colors">Contact</Link></li>
              </ul>
            </div>

            {/* Services */}
            <div>
              <h4 className="font-heading font-bold text-white uppercase mb-4 tracking-wider">Services</h4>
              <ul className="space-y-2 font-mono text-gray-400">
                <li><Link to="/enterprise-modernisation" className="hover:text-[#4A90E2] transition-colors">Enterprise Modernisation</Link></li>
                <li><Link to="/venture-product-engineering" className="hover:text-[#4A90E2] transition-colors">Venture Product Engineering</Link></li>
                <li><Link to="/services/aws" className="hover:text-[#4A90E2] transition-colors">AWS Services</Link></li>
                <li><Link to="/ai-maturity-framework" className="hover:text-[#4A90E2] transition-colors" data-testid="footer-framework-link">AI Maturity Framework™</Link></li>
              </ul>
            </div>

            {/* R&D Labs */}
            <div>
              <h4 className="font-heading font-bold text-white uppercase mb-4 tracking-wider">R&D LABS</h4>
              <ul className="space-y-2 font-mono text-gray-400">
                <li>
                  <a href="https://askdd.net/" target="_blank" rel="noopener noreferrer" className="hover:text-[#4A90E2] transition-colors">
                    AskDD: AI Sales Agent
                  </a>
                </li>
                <li>
                  <a href="http://www.serv-ai.com/" target="_blank" rel="noopener noreferrer" className="hover:text-[#4A90E2] transition-colors">
                    ServAI: Conversational Food Ordering
                  </a>
                </li>
              </ul>
            </div>

            {/* Legal */}
            <div>
              <h4 className="font-heading font-bold text-white uppercase mb-4 tracking-wider">Legal</h4>
              <ul className="space-y-2 font-mono text-gray-400">
                <li><Link to="/privacy-policy" className="hover:text-[#4A90E2] transition-colors" data-testid="footer-privacy-link">Privacy Policy</Link></li>
                <li><Link to="/terms-of-service" className="hover:text-[#4A90E2] transition-colors" data-testid="footer-terms-link">Terms of Service</Link></li>
              </ul>
            </div>
          </div>

          {/* Global footprint — three tiers per brand guide: HQ → Australia-wide → satellite offices */}
          <div
            className="pt-6 pb-6 mb-4 border-t border-b border-gray-800 flex flex-col gap-2 text-[10px] font-mono text-gray-500"
            data-testid="footer-nodes-strip"
          >
            <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1" data-testid="footer-tier-hq">
              <span className="uppercase tracking-widest text-gray-400">Tier 1 · HQ</span>
              <span className="flex items-center gap-1.5">
                <span aria-hidden="true">{getHQ().flag}</span>
                <span className="uppercase tracking-wider text-white/90">{getHQ().city}</span>
                <span className="font-mono text-[8px] uppercase tracking-widest px-1.5 py-0.5 text-white" style={{ background: '#9e591e' }}>HQ</span>
                <span className="text-gray-600">· Est. 2020</span>
              </span>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1" data-testid="footer-tier-national">
              <span className="uppercase tracking-widest text-gray-400">Tier 2 · Australia-wide</span>
              <span className="uppercase tracking-wider">
                {AU_STATES.map((st) => st.code).join(' · ')}
              </span>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1" data-testid="footer-tier-global">
              <span className="uppercase tracking-widest text-gray-400">Tier 3 · {getSatellites().length} satellite offices</span>
              {getSatellites().map((n, i, arr) => (
                <React.Fragment key={n.id}>
                  <span className="flex items-center gap-1.5">
                    <span aria-hidden="true">{n.flag}</span>
                    <span className="uppercase tracking-wider">{n.city}</span>
                  </span>
                  {i < arr.length - 1 && <span className="text-gray-700">·</span>}
                </React.Fragment>
              ))}
            </div>
          </div>

          {/* NAP — Name · Address · Phone (matches JSON-LD PostalAddress exactly) */}
          <address
            className="not-italic pb-6 mb-2 flex flex-col md:flex-row md:flex-wrap items-center justify-center gap-x-6 gap-y-1 text-[11px] font-mono text-gray-400 text-center"
            data-testid="footer-nap"
          >
            <span className="text-white/90 font-semibold tracking-wider uppercase">{NAP.legalName}</span>
            <span>{NAP.addressLine}</span>
            <a href={`tel:${NAP.phoneE164}`} className="hover:text-[#4A90E2] transition-colors">{NAP.phoneDisplay}</a>
            <a href={`mailto:${NAP.email}`} className="hover:text-[#4A90E2] transition-colors">{NAP.email}</a>
            <span>ABN {NAP.abn}</span>
          </address>

          {/* Bottom Bar */}
          <div className="flex flex-col md:flex-row justify-between items-center gap-4 pt-8 border-t border-gray-800 text-[10px] md:text-xs font-mono text-gray-500">
            <div className="flex flex-col md:flex-row items-center gap-4">
              <span className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></span>
                REF_ID: DD_CONSULT_2026
              </span>
              <span className="hidden md:inline">//</span>
              <span>STATUS: READY_TO_ENGAGE</span>
              <span className="hidden md:inline">//</span>
              <div className="flex gap-4">
                <a href="https://www.linkedin.com/company/dd-consulting-pty-ltd/" target="_blank" rel="noopener noreferrer" className="hover:text-[#4A90E2] transition-colors"><Linkedin className="w-4 h-4" /></a>
                <a href="https://www.facebook.com/profile.php?id=61584636043506" target="_blank" rel="noopener noreferrer" className="hover:text-[#4A90E2] transition-colors"><Facebook className="w-4 h-4" /></a>
                <a href="https://www.instagram.com/dd__consulting" target="_blank" rel="noopener noreferrer" className="hover:text-[#4A90E2] transition-colors"><Instagram className="w-4 h-4" /></a>
              </div>
            </div>
            <span>© 2026 DD CONSULTING | ABN {NAP.abn}</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
